import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { apiPost } from "@/api/api";
import { SEARCH_ERROR_MESSAGES } from "@/constants";
import type { GridPaginatedResponse } from "@/interfaces/GridPaginatedResponse";
import type QueryParams from "@/interfaces/QueryParams";
import type SearchGridSchema from "@/interfaces/SearchGridSchema";
import type { SearchRiskThresholds } from "@/interfaces/SearchGridSchema";
import type { SearchSortRule, UseFetchSearchResultsParams } from "@/interfaces/SearchInterfaces";
import Logger from "@/utils/Logger";

const getMapKey = (row: SearchGridSchema): string =>
	`${row.scmIDNum ?? ""}::${row.itemNumber ?? ""}`;

const buildThresholdsByMap = (items: SearchGridSchema[]): Record<string, SearchRiskThresholds> =>
	items.reduce<Record<string, SearchRiskThresholds>>((map, item) => {
		map[getMapKey(item)] = {
			thresholdHighMax: item.thresholdHighMax,
			thresholdMediumMin: item.thresholdMediumMin,
			thresholdMediumMax: item.thresholdMediumMax,
			thresholdLowMin: item.thresholdLowMin,
		};
		return map;
	}, {});

export const useFetchSearchResults = ({
	searchParams,
	searchTrigger,
	currentPage,
	pageSize,
	sortModel,
	filterModel,
}: UseFetchSearchResultsParams) => {
	const [loading, setLoading] = useState(false);
	const [latestRows, setLatestRows] = useState<SearchGridSchema[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [noResultsFound, setNoResultsFound] = useState(false);
	const [inlineError, setInlineError] = useState<string | null>(null);
	const [thresholdsByMap, setThresholdsByMap] = useState<Record<string, SearchRiskThresholds>>({});
	// Used to ignore stale responses when multiple concurrent requests are made.
	// Each request increments requestId; when a response comes back we compare to this ref
	// to determine if the response is still relevant.
	const activeRequestIdRef = useRef(0);
	// AbortController for the current in-flight request — aborted when a new request starts or on unmount.
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		// Only run search effect when searchTrigger > 0. The trigger increments on explicit user search.
		if (searchTrigger === 0) {
			return;
		}

		abortControllerRef.current?.abort();
		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		const requestId = activeRequestIdRef.current + 1;
		activeRequestIdRef.current = requestId;

		const queryBody: QueryParams = {
			...searchParams,
			startRow: currentPage * pageSize,
			endRow: currentPage * pageSize + pageSize,
		};
		// Keep threshold metadata scoped to the active search response and page.
		setThresholdsByMap({});
		const searchQueryBody: QueryParams & {
			sort?: SearchSortRule[];
			filter?: Record<string, unknown>;
		} = {
			...queryBody,
		};

		if (sortModel.length > 0) {
			searchQueryBody.sort = sortModel;
		}
		if (Object.keys(filterModel).length > 0) {
			searchQueryBody.filter = filterModel;
		}
		const performSearchPostCall = async () => {
			setLoading(true);
			setInlineError(null);

			try {
				const response = await apiPost<GridPaginatedResponse<SearchGridSchema>>(
					"/search",
					searchQueryBody,
					{
						signal: abortController.signal,
					},
				);

				if (activeRequestIdRef.current !== requestId) {
					return;
				}

				const items = response.responseObject?.items ?? [];
				const count = response.responseObject?.total ?? items.length;
				const thresholds = buildThresholdsByMap(items);

				setLatestRows(items);
				setThresholdsByMap(thresholds);
				setTotalCount(count);
				setNoResultsFound(count === 0);
			} catch (error) {
				if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
					return;
				}

				if (activeRequestIdRef.current !== requestId) {
					return;
				}

				Logger.error("Search API Error", error, ["useFetchSearchResults"]);
				setLatestRows([]);
				setThresholdsByMap({});
				setTotalCount(0);
				setNoResultsFound(false);
				setInlineError(SEARCH_ERROR_MESSAGES.SYSTEM_ERROR);
			} finally {
				if (activeRequestIdRef.current === requestId) {
					setLoading(false);
				}
			}
		};

		void performSearchPostCall();

		return () => {
			abortController.abort();
		};
	}, [currentPage, pageSize, searchParams, searchTrigger, sortModel, filterModel]);

	return {
		latestRows,
		thresholdsByMap,
		totalCount,
		loading,
		noResultsFound,
		inlineError,
		clearInlineError: () => {
			setInlineError(null);
		},
	};
};
